import styled, { css } from 'styled-components';
import { spacing } from '../../../styles/newThemeVariables.styles';
import { breakpointUp } from '../../../styles/mediaUtils';
import { DisplayMode } from './index';

const galleryAspectRatio = {
  singleVariant: {
    mobile: '145.33%', // 3:2 aspect ratio
    tablet: '99.50%', // 1:1 aspect ratio
    desktop: '52.34%', // 16:9 aspect ratio
    desktopLarge: '51.68%', // Slight variation of 16:9
  },
  bentoVariant: {
    desktop: '56.25%', // 16:9 aspect ratio
  },
};

const Item = styled.div`
  div {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: static;
  }

  video[poster] {
    object-fit: cover;
  }
`;

const singleVariantAutoContainerHeight = css`
  height: 100%;

  ${Item} {
    @media ${breakpointUp.desktop} {
      width: 100%;
      height: 100%;
    }
  }
`;

const singleVariantFixedContainerHeight = css`
  overflow: hidden;
  position: relative;
  height: 0;
  padding-bottom: ${galleryAspectRatio.singleVariant.mobile};

  @media ${breakpointUp.tablet} {
    padding-bottom: ${galleryAspectRatio.singleVariant.tablet};
  }

  @media ${breakpointUp.desktop} {
    padding-bottom: ${galleryAspectRatio.singleVariant.desktop};
  }

  @media ${breakpointUp.desktopLarge} {
    padding-bottom: ${galleryAspectRatio.singleVariant.desktopLarge};
  }

  ${Item} {
    height: 100%;
    width: 100%;
    position: absolute;
  }
`;

const bentoContainerHeight = css`
  @media ${breakpointUp.desktop} {
    width: 100%;
    height: 0;
    padding-bottom: ${galleryAspectRatio.bentoVariant.desktop};
    overflow: hidden;
    position: relative;

    ${Item} {
      height: 100%;
      width: 100%;
      position: absolute;
    }
  }
`;

const getContainerHeight = (variant: string, displayMode: DisplayMode) => {
  if (variant === 'single') {
    return displayMode === 'auto'
      ? singleVariantAutoContainerHeight
      : singleVariantFixedContainerHeight;
  }
  if (variant === 'trioBentoLeft' || variant === 'trioBentoRight') {
    return bentoContainerHeight;
  }
  return '';
};

export const Container = styled.div<{
  variant: string;
  displayMode: DisplayMode;
}>`
  display: ${({ variant }) => (variant === 'masonry' ? 'block' : 'grid')};
  grid-template-columns: repeat(12, 1fr);
  grid-column: 1 / -1;
  column-count: 1;
  margin: 0 -1rem;

  ${({ variant, displayMode }) => getContainerHeight(variant, displayMode)};

  ${({ variant }) => {
    const isSmallSpacing =
      variant === 'trioBentoLeft' ||
      variant === 'trioBentoRight' ||
      variant === 'double';

    return `
      column-gap: ${isSmallSpacing ? spacing.space2 : spacing.space24};
      row-gap: ${isSmallSpacing ? spacing.space2 : spacing.space24};
    `;
  }};

  ${({ variant, displayMode }) => {
    if (variant === 'single') {
      return `
      img, video {
        object-fit: ${displayMode} !important;
        @supports (-webkit-appearance: none) {
          ${displayMode !== 'auto' ? 'width: 100vw !important;' : ''}
        }
      }
    `;
    }

    return '';
  }};

  @media ${breakpointUp.tablet} {
    margin: 0;
    column-count: 2;
  }

  @media ${breakpointUp.desktop} {
    column-count: 3;
  }
`;

export const ItemSingle = styled(Item)`
  grid-column: 1 / -1;
`;

export const ItemDuo = styled(Item)`
  grid-column: 1 / -1;

  @media ${breakpointUp.tablet} {
    grid-column: span 6;
  }
`;

export const ItemDouble = styled(Item)`
  grid-column: 1 / -1;
  height: 0;
  width: 100%;
  padding-bottom: 92%;
  position: relative;

  img {
    object-fit: cover;
  }

  & > div {
    width: 100%;
    height: 100%;
    position: absolute;
  }

  @media ${breakpointUp.tablet} {
    padding-bottom: 59.72%;
  }
  @media ${breakpointUp.desktop} {
    grid-column: span 6;
    padding-bottom: 105.15%;
  }
`;

export const ItemDuoOffsetRight = styled(Item)`
  grid-column: 1 / -1;

  @media ${breakpointUp.tablet} {
    grid-column: span 8;

    &:first-child {
      grid-column: span 4;
    }
  }
`;

export const ItemDuoOffsetLeft = styled(Item)`
  grid-column: 1 / -1;

  @media ${breakpointUp.tablet} {
    grid-column: span 4;

    &:first-child {
      grid-column: span 8;
    }
  }
`;

export const ItemTrio = styled(Item)`
  grid-column: 1 / -1;

  @media ${breakpointUp.tablet} {
    grid-column: span 6;
  }

  @media ${breakpointUp.desktop} {
    grid-column: span 4;
  }
`;

const ItemBento = styled(Item)`
  img {
    object-fit: cover;
    object-position: center;
  }

  @media ${breakpointUp.desktop} {
    video {
      object-fit: cover;
      object-position: center;
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      z-index: 2;
    }
  }
`;

const bentoImagePosition = css`
  position: relative;

  & > div {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
`;

export const ItemTrioBentoLeft = styled(ItemBento)`
  &:first-child {
    grid-column: span 12;
    grid-row: 1 / 1;
  }

  &:nth-child(2) {
    grid-column: span 6;
    grid-row: 2 / 2;
  }

  &:nth-child(n + 3) {
    grid-column: span 6;
    grid-row: 2 / 2;
  }

  @media ${breakpointUp.desktop} {
    &:first-child {
      grid-column: span 6;
      grid-row: 1 / 3;

      ${bentoImagePosition};
    }

    &:nth-child(2) {
      grid-column: span 6;
      grid-row: 2 / 2;
      height: 0;
      padding-bottom: 56.25%;
      ${bentoImagePosition};
    }

    &:nth-child(n + 3) {
      grid-column: span 6;
      grid-row: 1 / 1;
      height: 0;
      padding-bottom: 56.25%;
      ${bentoImagePosition};
    }
  }
`;

export const ItemTrioBentoRight = styled(ItemBento)`
  &:first-child {
    grid-column: span 12;
    grid-row: 1 / 1;
  }

  &:nth-child(2) {
    grid-column: span 6;
    grid-row: 2 / 2;
  }

  &:nth-child(n + 3) {
    grid-column: span 6;
    grid-row: 2 / 2;
  }

  @media ${breakpointUp.desktop} {
    &:first-child {
      grid-column: 7 / span 6;
      grid-row: 1 / 3;
      ${bentoImagePosition};
    }

    &:nth-child(2) {
      grid-column: span 6;
      grid-row: 2 / 2;
      height: 0;
      padding-bottom: 56.25%;
      ${bentoImagePosition};
    }

    &:nth-child(n + 3) {
      grid-column: span 6;
      grid-row: 1 / 1;
      height: 0;
      padding-bottom: 56.25%;
      ${bentoImagePosition};
    }
  }
`;

export const ItemMasonry = styled(Item)`
  break-inside: avoid;
  margin-bottom: ${spacing.space24};
`;
